import type {
  Route,
  SidebarOptionName,
  Tags,
  TagName,
} from "@/types/UpNavRoutesType";

// Route Paths
const upNavRoutePaths = {
  // Root

  root: { name: "root", path: "/" },

  // Auth routes
  logout: { name: "logout", path: "/logout" },
  noAccess: { name: "noAccess", path: "/no-access" },
  expired: { name: "expired", path: "/expired" },

  // Mention
  mentionUpsell: { name: "mentionUpsell", path: "/upsell/mention" },
  mentionSearch: { name: "mentionSearch", path: "/search" },
  mentionMentions: { name: "mentionMentions", path: "/mentions" },
  mentionTopics: { name: "mentionTopics", path: "/topics" },
  mentionSocial: { name: "mentionSocial", path: "/social" },
  mentionAlertsRealTime: {
    name: "mentionAlertsRealTime",
    path: "/alerts/real-time",
  },
  mentionAlertsDigest: { name: "mentionAlertsDigest", path: "/alerts/digest" },
  mentionManage: { name: "mentionManage", path: "/manage" },
  mentionManageTargetMediaLists: {
    name: "mentionManageTargetMediaLists",
    path: "/target-media-lists",
  },
  mentionManageAddedMentions: {
    name: "mentionManageAddedMentions",
    path: "/manage/added-mentions",
  },
  mentionManageExcludedMentions: {
    name: "mentionManageExcludedMentions",
    path: "/manage/excluded-mentions",
  },
  mentionManageRequestedPublications: {
    name: "mentionManageRequestedPublications",
    path: "/manage/requested-publications",
  },
  mentionManageExcludedPublications: {
    name: "mentionManageExcludedPublications",
    path: "/manage/excluded-publications",
  },
  mentionManageUnlockedMentions: {
    name: "mentionManageUnlockedMentions",
    path: "/purchased-content",
  },
  mentionReports: { name: "mentionReports", path: "/reports" },
  mentionReportsList: { name: "mentionReportsList", path: "/reports#/list" },
  mentionReportsTemplates: {
    name: "mentionReportsTemplates",
    path: "/reports#/templates",
  },
  mentionReportsTemplatesNew: {
    name: "mentionReportsTemplatesNew",
    path: "/reports#/templates/new",
  },
  mentionReportsEdit: {
    name: "mentionReportsEdit",
    path: "/reports#/:id/edit",
  },
  mentionReportsTemplatesEdit: {
    name: "mentionReportsTemplatesEdit",
    path: "/reports#/templates/:id/edit",
  },

  chartAnalytics: {
    name: "chartAnalytics",
    path: "/charts",
    isExternalRoute: false,
  },

  chartAnalyticsReports: {
    name: "chartAnalyticsReports",
    path: "/pro/#/monitor/reports",
  },

  analyticsReports: {
    name: "analyticsReports",
    path: "/analytics/reports",
    isExternalRoute: true,
  },

  analyticsTemplates: {
    name: "analyticsTemplates",
    path: "/analytics/templates",
    isExternalRoute: true,
  },

  // Analytics
  analytics: { name: "analytics", path: "/analytics/", isExternalRoute: true },

  // Settings
  settings: { name: "settings", path: "/settings" },
};

// Tags
const tags: Record<TagName, Tags> = {
  new: {
    name: "new",
    translationKey: "navbar.tags.new",
  },
};

// child routes that do not appear in the navbar or tabs
const otherOptions = {
  reports: [
    {
      type: "otherOption",
      path: upNavRoutePaths.mentionReportsEdit.path,
      isExternalRoute: false,
      dataTarget: upNavRoutePaths.mentionReportsEdit.name,
    },
  ],
  reportsTemplates: [
    {
      type: "otherOption",
      path: upNavRoutePaths.mentionReportsTemplatesNew.path,
      isExternalRoute: false,
      dataTarget: upNavRoutePaths.mentionReportsTemplatesNew.name,
    },
    {
      type: "otherOption",
      path: upNavRoutePaths.mentionReportsTemplatesEdit.path,
      isExternalRoute: false,
      dataTarget: upNavRoutePaths.mentionReportsTemplatesEdit.name,
    },
  ],
  chartAnalytics: [
    {
      type: "otherOption",
      path: upNavRoutePaths.chartAnalyticsReports.path,
      isExternalRoute: true,
      dataTarget: upNavRoutePaths.chartAnalyticsReports.name,
    },
  ],
};

const tabOptions = {
  alerts: [
    {
      type: "tabOption",
      translationKey: "navbar.alerts.tabs.realTime",
      path: upNavRoutePaths.mentionAlertsRealTime.path,
      isExternalRoute: false,
      dataTarget: upNavRoutePaths.mentionAlertsRealTime.name,
    },
    {
      type: "tabOption",
      translationKey: "navbar.alerts.tabs.digest",
      path: upNavRoutePaths.mentionAlertsDigest.path,
      isExternalRoute: false,
      dataTarget: upNavRoutePaths.mentionAlertsDigest.name,
    },
  ],
  mentions: [
    {
      type: "tabOption",
      translationKey: "navbar.manage.tabs.excludedMentions",
      path: upNavRoutePaths.mentionManageExcludedMentions.path,
      isExternalRoute: false,
      dataTarget: upNavRoutePaths.mentionManageExcludedMentions.name,
    },
    {
      type: "tabOption",
      translationKey: "navbar.manage.tabs.requestedPublications",
      path: upNavRoutePaths.mentionManageRequestedPublications.path,
      isExternalRoute: false,
      dataTarget: upNavRoutePaths.mentionManageRequestedPublications.name,
    },
    {
      type: "tabOption",
      translationKey: "navbar.manage.tabs.excludedPublications",
      path: upNavRoutePaths.mentionManageExcludedPublications.path,
      isExternalRoute: false,
      dataTarget: upNavRoutePaths.mentionManageExcludedPublications.name,
    },
    {
      type: "tabOption",
      translationKey: "navbar.manage.mentionsAndPublications",
      path: upNavRoutePaths.mentionManageAddedMentions.path,
      isExternalRoute: false,
      dataTarget: upNavRoutePaths.mentionManageAddedMentions.name,
    },
  ],
};

const dropDownOptions = {
  manage: [
    {
      type: "navbarDropdownOption",
      translationKey: "navbar.manage.targetMediaLists",
      path: upNavRoutePaths.mentionManageTargetMediaLists.path,
      isExternalRoute: false,
      dataTarget: upNavRoutePaths.mentionManageTargetMediaLists.name,
    },
    {
      type: "navbarDropdownOption",
      translationKey: "common.alerts",
      path: upNavRoutePaths.mentionAlertsRealTime.path,
      isExternalRoute: false,
      dataTarget: upNavRoutePaths.mentionAlertsRealTime.name,
      childRoutes: [...tabOptions.alerts],
    },
    {
      type: "navbarDropdownOption",
      translationKey: "navbar.manage.manageSocialAccount",
      path: upNavRoutePaths.mentionSocial.path,
      isExternalRoute: false,
      dataTarget: upNavRoutePaths.mentionSocial.name,
    },
    {
      type: "navbarDropdownOption",
      translationKey: "navbar.manage.mentionsAndPublications",
      path: upNavRoutePaths.mentionManageAddedMentions.path,
      isExternalRoute: false,
      dataTarget: upNavRoutePaths.mentionManageAddedMentions.name,
      childRoutes: [...tabOptions.mentions],
    },
    {
      type: "navbarDropdownOption",
      translationKey: "navbar.manage.tabs.unlockedMentions",
      path: upNavRoutePaths.mentionManageUnlockedMentions.path,
      isExternalRoute: false,
      dataTarget: upNavRoutePaths.mentionManageUnlockedMentions.name,
      tags: [tags.new],
    },
    {
      type: "navbarDropdownOption",
      translationKey: "navbar.manage.manageSearch",
      path: upNavRoutePaths.mentionManage.path,
      isExternalRoute: false,
      dataTarget: upNavRoutePaths.mentionManage.name,
    },
  ],
  reports: [
    {
      type: "navbarDropdownOption",
      translationKey: "common.reports",
      path: upNavRoutePaths.mentionReportsList.path,
      isExternalRoute: true,
      dataTarget: upNavRoutePaths.mentionReportsList.name,
      dropdownColumnKey: "coverage",
      childRoutes: [...otherOptions.reports],
    },
    {
      type: "navbarDropdownOption",
      translationKey: "common.templates",
      path: upNavRoutePaths.mentionReportsTemplates.path,
      isExternalRoute: true,
      dataTarget: upNavRoutePaths.mentionReportsTemplates.name,
      dropdownColumnKey: "coverage",
      childRoutes: [...otherOptions.reportsTemplates],
    },
    {
      type: "navbarDropdownOption",
      translationKey: "common.reports",
      path: upNavRoutePaths.analyticsReports.path,
      isExternalRoute: true,
      dataTarget: upNavRoutePaths.analyticsReports.name,
      dropdownColumnKey: "analytics",
    },
    {
      type: "navbarDropdownOption",
      translationKey: "common.templates",
      path: upNavRoutePaths.analyticsTemplates.path,
      isExternalRoute: true,
      dataTarget: upNavRoutePaths.analyticsTemplates.name,
      dropdownColumnKey: "analytics",
    },
  ],
};

const navbarOptions = {
  mention: [
    {
      type: "navbarOption",
      translationKey: "upNavigation.mention.search",
      path: upNavRoutePaths.mentionSearch.path,
      isExternalRoute: false,
      dataTarget: upNavRoutePaths.mentionSearch.name,
    },
    {
      type: "navbarOption",
      translationKey: "upNavigation.mention.allMentions",
      path: upNavRoutePaths.mentionMentions.path,
      isExternalRoute: false,
      dataTarget: upNavRoutePaths.mentionMentions.name,
    },
    {
      type: "navbarOption",
      translationKey: "upNavigation.mention.topics",
      path: upNavRoutePaths.mentionTopics.path,
      isExternalRoute: false,
      dataTarget: upNavRoutePaths.mentionTopics.name,
    },
    {
      type: "navbarOption",
      translationKey: "upNavigation.mention.manage",
      path: upNavRoutePaths.mentionManage.path,
      isExternalRoute: false,
      dataTarget: upNavRoutePaths.mentionManage.name,
      childRoutes: [...dropDownOptions.manage],
    },
    {
      type: "navbarOption",
      translationKey: "upNavigation.mention.reports",
      path: upNavRoutePaths.mentionReports.path,
      isExternalRoute: true,
      dataTarget: upNavRoutePaths.mentionReports.name,
      childRoutes: [...dropDownOptions.reports],
    },
    {
      type: "navbarOption",
      translationKey: "upNavigation.mention.chartAnalytics",
      isExternalRoute: false,
      dataTarget: upNavRoutePaths.chartAnalytics.name,
      path: upNavRoutePaths.chartAnalytics.path,
      childRoutes: [...otherOptions.chartAnalytics],
    },
  ],
  settings: [
    {
      type: "navbarOption",
      translationKey: "upNavigation.mention.settings",
      path: upNavRoutePaths.settings.path,
      isExternalRoute: false,
      dataTarget: upNavRoutePaths.settings.name,
    },
  ],
};

const sidebarOptions: Partial<Record<SidebarOptionName, Route>> = {
  Mention: {
    name: "Mention",
    type: "sidebarOption",
    dataTarget: upNavRoutePaths.mentionSearch.name,
    translationKey: "upNavigation.mention.mention",
    icon: "material-symbols:browse-activity-outline",
    isPrefix: true,
    path: upNavRoutePaths.mentionSearch.path,
    upsellPath: upNavRoutePaths.mentionUpsell.path,
    childRoutes: [...navbarOptions.mention] as Route[],
  },
  Review: {
    name: "Review",
    type: "sidebarOption",
    dataTarget: "Review",
    translationKey: "common.review",
    icon: "material-symbols:newsmode-outline",
    isPrefix: true,
  },
  Contact: {
    name: "Contact",
    type: "sidebarOption",
    dataTarget: "Contact",
    translationKey: "common.contact",
    icon: "material-symbols:contact-page-outline",
    isPrefix: true,
  },
  Measure: {
    name: "Measure",
    type: "sidebarOption",
    dataTarget: "Measure",
    translationKey: "common.measure",
    icon: "material-symbols:area-chart-outline",
    isPrefix: true,
  },
  Settings: {
    name: "Settings",
    type: "sidebarOption",
    dataTarget: upNavRoutePaths.settings.name,
    translationKey: "sidebar.settings",
    icon: "material-symbols:manufacturing",
    isPrefix: false,
    path: upNavRoutePaths.settings.path,
    childRoutes: [...navbarOptions.settings] as Route[],
  },
  Help: {
    name: "Help",
    type: "sidebarOption",
    dataTarget: "Help Button",
    translationKey: "sidebar.help",
    icon: "material-symbols:help-outline",
    isPrefix: false,
  },
  Logout: {
    name: "Logout",
    type: "sidebarOption",
    dataTarget: "Logout",
    translationKey: "sidebar.logout",
    icon: "material-symbols:logout",
    isPrefix: false,
  },
};

export { upNavRoutePaths, sidebarOptions as upNavRouteConfig };
